import { type ClipboardEvent, type DragEvent, useState } from 'react';

/**
 * Files handed to the composer by its picker, pasted into the field or
 * dropped onto it. `accepting` says whether it takes them right now; without
 * `onAttach` the composer has no upload at all and leaves these events alone.
 */
export function useComposerFiles(
  onAttach: ((files: File[]) => void) | undefined,
  accepting: boolean,
) {
  const [dropping, setDropping] = useState(false);
  const uploads = onAttach !== undefined;

  function take(files: FileList | null) {
    const picked = [...(files ?? [])];
    if (picked.length > 0) {
      onAttach?.(picked);
    }
  }

  /*
   * A screenshot goes to the clipboard as a file, so pasting one is the fastest
   * way there is to share it. Text pastes are left to the field.
   */
  function onPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    if (!uploads || !accepting) {
      return;
    }

    const files = [...event.clipboardData.files];
    if (files.length > 0) {
      event.preventDefault();
      onAttach?.(files);
    }
  }

  function onDrop(event: DragEvent<HTMLFormElement>) {
    if (!uploads) {
      return;
    }

    event.preventDefault();
    setDropping(false);
    if (accepting) {
      take(event.dataTransfer.files);
    }
  }

  function onDragOver(event: DragEvent<HTMLFormElement>) {
    if (!uploads || !event.dataTransfer.types.includes('Files')) {
      return;
    }

    event.preventDefault();
    setDropping(true);
  }

  function onDragLeave() {
    setDropping(false);
  }

  return { uploads, dropping, take, onPaste, onDrop, onDragOver, onDragLeave };
}
